import React from 'react';

/**
 * Minimal markdown renderer for **bold** + auto-link URLs.
 * - `**text**`  →  <strong>text</strong>
 * - URLs (http/https/www.)  →  <a href> in new tab
 * - `**URL**`  →  <strong><a href>URL</a></strong>  (both at once)
 * - newlines  →  <br>
 *
 * No other HTML is interpreted (angle brackets remain literal) → XSS-safe.
 */
export default function MarkdownText({ text, className = '' }) {
  if (!text) return null;
  const lines = String(text).split('\n');
  return (
    <span className={className} data-testid="markdown-text">
      {lines.map((line, li) => (
        <React.Fragment key={li}>
          {renderLine(line, li)}
          {li < lines.length - 1 && <br />}
        </React.Fragment>
      ))}
    </span>
  );
}

const URL_RE = /(?:https?:\/\/|www\.)[^\s<>()]+[^\s<>().,;:!?]/g;
const BOLD_RE = /\*\*([^*]+)\*\*/g;

/** Replace URLs in a plain string with <a> elements. Returns ReactNode[]. */
function autoLink(str, keyPrefix) {
  const out = [];
  let last = 0;
  let k = 0;
  let m;
  URL_RE.lastIndex = 0;
  while ((m = URL_RE.exec(str)) !== null) {
    if (m.index > last) {
      out.push(<React.Fragment key={`${keyPrefix}-t-${k++}`}>{str.slice(last, m.index)}</React.Fragment>);
    }
    const raw = m[0];
    const href = raw.startsWith('http') ? raw : `https://${raw}`;
    out.push(
      <a
        key={`${keyPrefix}-a-${k++}`}
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className="text-orange-600 hover:text-orange-700 underline break-all"
      >
        {raw}
      </a>
    );
    last = m.index + raw.length;
  }
  if (last < str.length) {
    out.push(<React.Fragment key={`${keyPrefix}-t-${k++}`}>{str.slice(last)}</React.Fragment>);
  }
  return out;
}

function renderLine(line, lineIdx) {
  // First pass: split by **bold** while keeping non-bold text in place.
  const out = [];
  let last = 0;
  let k = 0;
  let m;
  BOLD_RE.lastIndex = 0;
  while ((m = BOLD_RE.exec(line)) !== null) {
    if (m.index > last) {
      const plain = line.slice(last, m.index);
      out.push(<React.Fragment key={`${lineIdx}-p-${k++}`}>{autoLink(plain, `${lineIdx}-p${k}`)}</React.Fragment>);
    }
    out.push(
      <strong key={`${lineIdx}-b-${k++}`}>{autoLink(m[1], `${lineIdx}-b${k}`)}</strong>
    );
    last = m.index + m[0].length;
  }
  if (last < line.length) {
    const tail = line.slice(last);
    out.push(<React.Fragment key={`${lineIdx}-p-${k++}`}>{autoLink(tail, `${lineIdx}-t${k}`)}</React.Fragment>);
  }
  return out;
}
